// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSystemColor } from "@turnerBlueprint/XDL/Utilities";

import React, { FC } from "react";

// eslint-disable-next-line spire/fenced-imports
import { css } from "styled-components";

const enum fields {
    callToActionImage = "callToActionImage",
    callToActionButtonLink = "callToActionButtonLink",
    callToActionButtonText = "callToActionButtonText",
}

export interface ServiceShowcaseStyles {
    gridContainerStyles: InjectableCss;
    gridItemHeaderImageStyles: InjectableCss;
    serviceContainerStyles: InjectableCss;
    serviceImageContainerStyles: InjectableCss;
    imageTileStyles: InjectableCss;
}

export const serviceShowcaseStyles: ServiceShowcaseStyles = {
    gridContainerStyles: {
        css: css`
            @media print {
                max-width: 100%;
            }
            background: #f1f1f1;
        `,
    },
    gridItemHeaderImageStyles: {
        css: css`
            padding-bottom: 0px;
        `,
    },
    imageTileStyles: {
        css: css`
            padding: 0.5em;
        `,
    },
    serviceContainerStyles: {
        css: css`
            margin-top: 1em !important;
        `,
    },
    serviceImageContainerStyles: {
        css: css`
            margin-top: 1em !important;
            margin-left: 0px;
            padding-left: 0px;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.callToActionImage]: string;
        [fields.callToActionButtonLink]: string;
        [fields.callToActionButtonText]: string;
    };
}

type Props = OwnProps & HasHistory;

const styles = serviceShowcaseStyles;
const CallToActionTile: FC<Props> = ({ fields, history }) => {
    const routeToClick = () => {
        window.location.href = fields.callToActionButtonLink;
    };
    return (
        <StyledWrapper data-test-selector="custom_content_call_to_action">
            <GridContainer style={{ paddingTop: "15px", paddingBottom: "15px" }} className="custom_call_to_action_grid">
                <GridItem width={12} style={{ padding: "0", marginRight: "0px", marginLeft: "0px" }}>
                    <a href={fields.callToActionButtonLink} style={{ lineHeight: 0 }}>
                        <LazyImage src={fields.callToActionImage} altText={fields.callToActionButtonText} />
                    </a>
                </GridItem>
                <GridItem width={10} style={{ padding: "0", backgroundColor: `${getSystemColor("common.ui2")}` }}>
                    <Button
                        size={60}
                        onClick={routeToClick}
                        hoverStyle={{ background: `${getSystemColor("common.ui2")}`, borderColor: `${getSystemColor("common.ui2")}` }}
                        typographyProps={{ size: 24, weight: 600 }}
                        css={css`
                            padding: 0.5em 1em;
                            background-color: ${getColor("common.ui2")};
                            background: ${getColor("common.ui2")};
                            border: 0em solid ${getColor("common.ui2")};
                            width: 100%;
                            text-align: left;
                        `}
                    >
                        {fields.callToActionButtonText}
                    </Button>
                </GridItem>
                <GridItem width={2} style={{ padding: "0" }}>
                    <Button
                        size={60}
                        onClick={routeToClick}
                        typographyProps={{ weight: 600 }}
                        hoverStyle={{ background: `${getSystemColor("common.ui2")}`, borderColor: `${getSystemColor("common.ui2")}` }}
                        css={css`
                            padding: 0.5em 1em;
                            background-color: ${getColor("common.ui2")};
                            background: ${getColor("common.ui2")};
                            border: 0em solid ${getColor("common.ui2")};
                            width: 100%;
                            text-align: right;
                        `}
                    >
                        <Icon src={ArrowRight} />
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(CallToActionTile),
    definition: {
        displayName: "Call To Action Tile",
        icon: "Flag",
        fieldDefinitions: [
            {
                name: fields.callToActionImage,
                displayName: "Call To Action Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                tooltip: "The image to show on the call to action. Max height 600px, max width 600px.",
            },
            {
                name: fields.callToActionButtonLink,
                displayName: "Call To Action Button Link",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The URL for the call to action button.",
                placeholder: "",
                isRequired: true,
            },
            {
                name: fields.callToActionButtonText,
                displayName: "Call To Action Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The text to show on the call to action button.",
                placeholder: "",
                isRequired: true,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
